/**
 * R20 Amenity booking — redesign v3 "Amenity booking" frame: colour-coded
 * slot grid (open / booked, legend below) instead of the list-row layout,
 * free slot → confirm Sheet → POST book. 409 (slot taken) shows an inline
 * error and refreshes the grid instead of closing the sheet.
 * NOTE (deviation, flagged): the design frame also shows a day-of-week strip
 * (Fri…Tue) above the grid. GET /api/bookings/amenities only exposes TODAY's
 * slot map (no date param), so the day strip isn't wired here — reproducing
 * it would either be decorative-only or silently drop the "different day"
 * feature. Left out; today's date is shown in the card header instead, same
 * as before.
 * Port of design/source/screens-misc.jsx AmenitiesScreen + ConfirmBookingSheet.
 */
import { router } from 'expo-router';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { formatINR, radius, space } from '@bero/tokens';
import {
  backOr, CardSkeletons, longDate, resolvedAmenityIcon, slotRangeCompact, slotRangeLabel,
} from '@/components/money/bits';
import {
  bookAmenitySlot, errorMessage, fetchAmenities, isConflict, useAsync, type Amenity, type AmenitySlot,
} from '@/components/money/api';
import { Icon } from '@/components/money/icons';
import { useT } from '@/i18n';
import { useSession } from '@/lib/session';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Screen, Sheet, TopBar } from '@/ui';

type Target = { amenity: Amenity; slotStart: string; slotEnd: string | null };

export default function AmenitiesScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { user } = useSession();
  const flatSuffix = user?.flat?.code ? ` (${user.flat.code})` : '';
  const { data: amenities, loading, error, refetch, mutate } = useAsync(fetchAmenities, []);

  const [target, setTarget] = React.useState<Target | null>(null);
  const [justBooked, setJustBooked] = React.useState<Target | null>(null);
  const [booking, setBooking] = React.useState(false);
  const [bookError, setBookError] = React.useState<string | null>(null);

  const openConfirm = (amenity: Amenity, slotStart: string, slotEnd: string | null) => {
    setBookError(null);
    setTarget({ amenity, slotStart, slotEnd });
  };

  const closeSheet = () => {
    setTarget(null);
    setJustBooked(null);
  };

  const confirm = async () => {
    if (!target || booking) return;
    const { amenity, slotStart } = target;
    setBooking(true);
    setBookError(null);
    try {
      await bookAmenitySlot(amenity.id, amenity.date, slotStart);
      mutate((prev) => (prev ?? []).map((a) => (a.id !== amenity.id ? a : {
        ...a,
        slots: a.slots.map((s) => (s.slotStart === slotStart ? { ...s, state: 'booked', bookedBy: user?.flat?.code ?? 'You' } : s)),
      })));
      setJustBooked(target);
      setTarget(null);
    } catch (e) {
      if (isConflict(e)) {
        setBookError(t('am_conflict'));
        void refetch();
      } else {
        setBookError(errorMessage(e, t('am_book_error')));
      }
    } finally {
      setBooking(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('am_title')}
        onBack={backOr('/(tabs)/home')}
        right={<AppText variant="bodySm" weight="600" tone="brand" onPress={() => router.push('/my-bookings')} style={{ paddingRight: space.s2 }}>{t('am_my_bookings')}</AppText>}
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {loading ? (
          <CardSkeletons count={3} h={160} />
        ) : error ? (
          <EmptyState
            title={t('am_err_title')}
            body={t('am_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : !amenities || amenities.length === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={t('am_empty_title')}
            body={t('am_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s4 }}>
            {amenities.map((a) => (
              <Card key={a.id}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                  <View style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: colors.canvas1, alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={resolvedAmenityIcon(a)} size={20} color={colors.textSecondary} />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <AppText variant="headingSm" weight="500">{a.name}</AppText>
                      {a.isPaid ? (
                        <View style={{ backgroundColor: colors.canvas2, borderRadius: 8, paddingHorizontal: 6, paddingVertical: 2 }}>
                          <AppText variant="bodySm" mono tone="secondary">{formatINR(a.feePaise / 100)}{t('am_per_slot')}</AppText>
                        </View>
                      ) : null}
                    </View>
                    <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{t('am_today_date', { date: longDate(a.date) })}</AppText>
                  </View>
                </View>

                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: space.s4 }}>
                  {a.slots.map((s) => (
                    <SlotCell
                      key={s.slotStart}
                      slot={s}
                      onPress={() => openConfirm(a, s.slotStart, s.slotEnd)}
                    />
                  ))}
                </View>

                <View style={{ flexDirection: 'row', gap: space.s4, marginTop: space.s3 }}>
                  <Legend swatch={colors.canvas0} border={colors.borderSoft} label={t('am_open')} />
                  <Legend swatch={colors.canvas2} label={t('am_taken')} />
                </View>

                {a.rules.length > 0 ? (
                  <View style={{ marginTop: space.s3, paddingTop: space.s3, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: colors.borderSoft, gap: 4 }}>
                    <AppText variant="label" tone="tertiary">{t('am_rules')}</AppText>
                    {a.rules.map((r) => (
                      <AppText key={r} variant="bodySm" tone="secondary">· {r}</AppText>
                    ))}
                  </View>
                ) : null}
              </Card>
            ))}
          </View>
        )}
      </Screen>

      <Sheet visible={!!target || !!justBooked} onClose={closeSheet}>
        {target ? (
          <>
            <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>{t('am_confirm')}</AppText>
            <View style={{ gap: space.s4 }}>
              <Row icon={resolvedAmenityIcon(target.amenity)} label={target.amenity.name} />
              <Row icon="calendar" label={longDate(target.amenity.date)} />
              <Row icon="clock" label={slotRangeLabel(target.slotStart, target.slotEnd)} />
              <Row icon="user" label={t('am_booked_by', { name: `${user?.fullName ?? t('am_you')}${flatSuffix}` })} />
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <AppText variant="bodyMd" tone="secondary">{t('am_charges')}</AppText>
                {target.amenity.isPaid ? (
                  <AppText variant="bodyMd" mono weight="500">{formatINR(target.amenity.feePaise / 100)}</AppText>
                ) : (
                  <AppText variant="bodyMd" tone="tertiary">{t('am_free')}</AppText>
                )}
              </View>
            </View>
            {/* TODO(wire): real payment gateway — paid amenities currently book for
                free like standard slots; the fee above is informational only until
                a payment gateway is wired (see apps/api bookings.py book_amenity). */}
            {bookError ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s3 }}>{bookError}</AppText> : null}
            <Button title={t('am_confirm')} size="lg" loading={booking} onPress={() => void confirm()} style={{ marginTop: space.s5 }} />
            <Button title={t('cancel')} kind="tertiary" onPress={() => setTarget(null)} style={{ marginTop: 4 }} />
          </>
        ) : justBooked ? (
          <View style={{ alignItems: 'center' }}>
            <Icon name="check-circle" size={44} color={colors.primary500} />
            <AppText variant="headingSm" weight="500" style={{ marginTop: space.s4, textAlign: 'center' }}>
              {t('am_slot_booked')}
            </AppText>
            <AppText variant="bodyMd" tone="secondary" style={{ marginTop: 6, textAlign: 'center' }}>
              {justBooked.amenity.name} · {slotRangeLabel(justBooked.slotStart, justBooked.slotEnd)}
            </AppText>
            <Button title={t('am_view_bookings')} size="lg" onPress={() => { closeSheet(); router.push('/my-bookings'); }} style={{ marginTop: space.s5, alignSelf: 'stretch' }} />
            <Button title={t('am_done')} kind="tertiary" onPress={closeSheet} style={{ marginTop: 4, alignSelf: 'stretch' }} />
          </View>
        ) : null}
      </Sheet>
    </Screen>
  );
}

/* ---------------- colour-coded slot cell (grid) ---------------- */
function SlotCell({ slot, onPress }: { slot: AmenitySlot; onPress: () => void }) {
  const { colors } = useTheme();
  const booked = slot.state === 'booked';
  return (
    <View style={{ width: '31%' }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={slotRangeLabel(slot.slotStart, slot.slotEnd)}
        disabled={booked}
        onPress={onPress}
        style={({ pressed }) => ({
          height: 46, borderRadius: radius.md, alignItems: 'center', justifyContent: 'center',
          borderWidth: booked ? 0 : 1, borderColor: colors.borderSoft,
          backgroundColor: booked ? colors.canvas2 : pressed ? colors.primary50 : colors.canvas0,
          opacity: booked ? 0.7 : 1,
        })}
      >
        <AppText variant="bodySm" weight="500" numberOfLines={1}
          style={{ color: booked ? colors.textTertiary : colors.textPrimary }}>
          {slotRangeCompact(slot.slotStart, slot.slotEnd)}
        </AppText>
      </Pressable>
      {booked && slot.bookedBy ? (
        <AppText variant="bodySm" tone="tertiary" style={{ textAlign: 'center', fontSize: 11.5, marginTop: 2 }} numberOfLines={1}>
          {slot.bookedBy}
        </AppText>
      ) : null}
    </View>
  );
}

function Legend({ swatch, border, label }: { swatch: string; border?: string; label: string }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
      <View style={{
        width: 9, height: 9, borderRadius: 3, backgroundColor: swatch,
        borderWidth: border ? 1 : 0, borderColor: border,
      }} />
      <AppText variant="bodySm" tone="tertiary">{label}</AppText>
    </View>
  );
}

function Row({ icon, label }: { icon: Parameters<typeof Icon>[0]['name']; label: string }) {
  const { colors } = useTheme();
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
      <Icon name={icon} size={18} color={colors.textSecondary} />
      <AppText variant="bodyMd" style={{ flex: 1 }}>{label}</AppText>
    </View>
  );
}
